import { FC, useCallback, useEffect, useState } from 'react';
import { Loader } from '../../../components';
import TransactionLabelField from '../../../components/transaction/TransactionLabelField';
import ListOnFarmStorageLocations from './list-storage-locations/ListOnFarmStorageLocations';
import { getOnFarmSpecimenById } from '../../../api/onFarmSpecimenApi';
import { showToast } from '../../../services/toast.service';
import { OnFarmSpecimen, OnFarmSpecimenCustomDataValue } from '../../../types/interfaces/onFarmSpecimen.interfaces';
import { ANIMAL_CONSTANTS, INVENTORY_CONSTANTS, LABEL_CONSTANTS } from '../../../constants/common';
import { toastMessages } from '../../../constants/errorMessages';

type OnFarmSpecimenViewEditProps = {
  onFarmSpecimenId: number;
};

const OnFarmSpecimenViewEdit: FC<OnFarmSpecimenViewEditProps> = ({ onFarmSpecimenId }): JSX.Element => {
  const [specimen, setSpecimen] = useState<OnFarmSpecimen>();
  const [isLoading, setIsLoading] = useState(true);

  const isEmbryo = specimen?.specimenType?.name?.toLowerCase() === 'embryo';

  const loadSpecimen = useCallback(async () => {
    if (!onFarmSpecimenId) {
      return;
    }

    try {
      setIsLoading(true);
      const { data: specimenData } = await getOnFarmSpecimenById(onFarmSpecimenId, {
        include: 'OnFarmAnimal,SpecimenType,FrozenAtSite,OnFarmSpecimenCustomDataValues.SpecimenCustomDataKey',
      });
      setSpecimen(specimenData);
    } catch {
      showToast.error(toastMessages.FAILED_TO_RETRIEVE_SPECIMEN);
    } finally {
      setIsLoading(false);
    }
  }, [onFarmSpecimenId]);

  useEffect(() => {
    loadSpecimen();
  }, [loadSpecimen]);

  if (isLoading) {
    return (
      <div className="specimen-view-edit">
        <Loader loaderSize="medium" pageLoader />
      </div>
    );
  }

  if (!specimen) {
    return (
      <div className="specimen-view-edit">
        <div className="inventory-action card">
          <label>{LABEL_CONSTANTS.NO_RESULTS_FOUND}</label>
        </div>
      </div>
    );
  }

  const animalLabel = specimen.onFarmAnimal ? `${specimen.onFarmAnimal.code ?? ''}-${specimen.onFarmAnimal.name ?? ''}` : 'N/A';

  return (
    <div className="specimen-view-edit">
      <div className="inventory-action card">
        <h2>{INVENTORY_CONSTANTS.ITEM_DETAILS}</h2>
        <TransactionLabelField
          label={`${INVENTORY_CONSTANTS.LOT_NUMBER}:`}
          value={<label>{specimen.onFarmSpecimenId ?? '--'}</label>}
        />
        <TransactionLabelField
          label={`${LABEL_CONSTANTS.USER_LOT_NUMBER}:`}
          value={<label>{specimen.lotNumber || '--'}</label>}
        />
        <TransactionLabelField label={`${INVENTORY_CONSTANTS.ANIMAL}:`} value={<label>{animalLabel}</label>} />
        <TransactionLabelField
          label={`${INVENTORY_CONSTANTS.ITEM_TYPE}:`}
          value={<label>{specimen.specimenType?.name ?? 'N/A'}</label>}
        />
        {(specimen.onFarmSpecimenCustomDataValues ?? []).map((data: OnFarmSpecimenCustomDataValue) => {
          if (!data.specimenCustomDataKey) {
            return null;
          }

          return (
            <TransactionLabelField
              key={`${data.specimenCustomDataKeyId}-${data.specimenCustomDataKey.keyName}`}
              label={`${data.specimenCustomDataKey.keyName}:`}
              value={<label>{data.value || '--'}</label>}
            />
          );
        })}
        <TransactionLabelField
          label={`${INVENTORY_CONSTANTS.ITEM_UNIT_MEASURE}:`}
          value={<label>{specimen.specimenUOM ?? 'N/A'}</label>}
        />
        <TransactionLabelField label={`${INVENTORY_CONSTANTS.QUALITY}:`} value={<label>{specimen.quality ?? 'N/A'}</label>} />
        <TransactionLabelField
          label={`${INVENTORY_CONSTANTS.QUALITY} %:`}
          value={<label>{(specimen.qualityPercentage ?? 'N/A').toString()}</label>}
        />
        <TransactionLabelField
          label={`${INVENTORY_CONSTANTS.LOT_DATE} :`}
          value={<label>{specimen.freezeDate ? new Date(specimen.freezeDate).toLocaleDateString() : 'N/A'}</label>}
        />
        <TransactionLabelField
          label={`${INVENTORY_CONSTANTS.COLLECTION_SITE} :`}
          value={<label>{specimen.frozenAtSite?.name ?? 'N/A'}</label>}
        />
        <TransactionLabelField
          label={isEmbryo ? `${INVENTORY_CONSTANTS.EMBRYOLOGIST} :` : `${INVENTORY_CONSTANTS.COLLECTED_BY} :`}
          value={<label>{isEmbryo ? specimen.embryologistName || 'N/A' : specimen.collectedBy?.toString() || 'N/A'}</label>}
        />
        <TransactionLabelField
          label={isEmbryo ? `${INVENTORY_CONSTANTS.TECHNICIAN} :` : `${INVENTORY_CONSTANTS.PROCESSED_BY} :`}
          value={<label>{isEmbryo ? specimen.technicianName || 'N/A' : specimen.processedBy?.toString() || 'N/A'}</label>}
        />
        <TransactionLabelField
          label={isEmbryo ? `${INVENTORY_CONSTANTS.VETERINARIAN} :` : `${INVENTORY_CONSTANTS.FROZEN_BY} :`}
          value={<label>{isEmbryo ? specimen.veterinarianName || 'N/A' : specimen.frozenBy?.toString() || 'N/A'}</label>}
        />
        <TransactionLabelField
          label={`${INVENTORY_CONSTANTS.ORIGINAL_QUANTITY}:`}
          value={<label>{specimen.originalQuantity ?? 'N/A'}</label>}
        />
        <div className="form-row expanded-text-area">
          <label htmlFor="notes">{LABEL_CONSTANTS.NOTES}:</label>
          <pre>{specimen.notes || '--'}</pre>
        </div>

        <h2>{ANIMAL_CONSTANTS.STORAGE_LOCATIONS}</h2>
        <ListOnFarmStorageLocations onFarmSpecimenId={onFarmSpecimenId} />
      </div>
    </div>
  );
};

export default OnFarmSpecimenViewEdit;
